import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Building2, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const ROLE_LABEL = { admin: "hc.adminFullView", staff: "hc.staff" };

export default function ActivateMember() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError(t("hc.invalidInvitationLink")); setVerifying(false); return; }
    (async () => {
      try {
        const r = await api(`/team/invite/verify?token=${encodeURIComponent(token)}`, { auth: false });
        setInvite(r);
      } catch (e) {
        setError(e.message || t("hc.invalidInviteLink"));
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pwd !== confirm) { toast.error(t("hc.passwordsDoNotMatch")); return; }
    setLoading(true);
    try {
      await api("/team/invite/activate", { method: "POST", body: { token, password: pwd }, auth: false });
      toast.success(t("hc.accountActivatedPleaseLogIn"));
      navigate("/auth/login");
    } catch (err) {
      toast.error(err.message || "Activation impossible.");
    } finally {
      setLoading(false);
    }
  }

  if (verifying) {
    return (
      <AuthShell title={t("hc.accountActivation")} subtitle={t("hc.verifyingInvite")}>
        <div className="flex justify-center py-6" data-testid="activate-verifying">
          <FindoSpinner size={28} />
        </div>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell title={t("hc.invalidInvitationLink2")} subtitle={error}>
        <div className="text-sm text-slate-600" data-testid="activate-error">
          <Link to="/auth/login" className="font-semibold text-findo-indigo">{t("hc.goToLogin")}</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Bienvenue${invite?.name ? `, ${invite.name}` : ""}`}
      subtitle={t("hc.inviteSubtitle").replace("{org}", invite?.org_name || t("hc.anOrganization"))}>
      <div className="rounded-xl bg-findo-indigo/5 border border-findo-indigo/15 p-4 mb-5 space-y-1.5 text-sm" data-testid="activate-context">
        <p className="text-slate-700"><span className="text-slate-400">Email :</span> <span className="font-semibold">{invite?.email}</span></p>
        <p className="text-slate-700 flex items-center gap-1.5"><ShieldCheck size={14} className="text-findo-indigo" /> {t("hc.role")} <span className="font-semibold">{ROLE_LABEL[invite?.role] ? t(ROLE_LABEL[invite?.role]) : invite?.role}</span></p>
        {invite?.establishment_name && (
          <p className="text-slate-700 flex items-center gap-1.5"><Building2 size={14} className="text-findo-indigo" /> {t("hc.establishment2")} <span className="font-semibold">{invite.establishment_name}</span></p>
        )}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
        <div>
          <Label htmlFor="pwd">{t("hc.password")}</Label>
          <PasswordInput id="pwd" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder={t("hc.atLeast6Characters")} className="mt-1.5 h-11 rounded-xl" data-testid="activate-password" />
        </div>
        <div>
          <Label htmlFor="confirm">{t("hc.confirmPassword")}</Label>
          <PasswordInput id="confirm" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("hc.repeatThePassword")} className="mt-1.5 h-11 rounded-xl" data-testid="activate-confirm" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold" data-testid="activate-submit">
          {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("hc.activating")}</span> : t("hc.activateMyAccount")}
        </Button>
      </form>
    </AuthShell>
  );
}
